// src/components/common/BugReportButton.js
// Bug-report icon + modal. Posts to /api/support/bug-report which fans out
// to the support inbox via Resend.
import React, { useState } from 'react';
import { Bug, X, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import supportService from '../../services/support.service';

const SEVERITIES = [
    { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
];

const BugReportButton = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const close = () => {
        if (submitting) return;
        setOpen(false);
        setError(null);
        // Keep fields if user is just dismissing successful flow — reset on next open
        if (success) {
            setTitle('');
            setDescription('');
            setSeverity('medium');
            setSuccess(false);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setError(null);
        const t = title.trim();
        const d = description.trim();
        if (!t || !d) {
            setError('Please add a short title and a description.');
            return;
        }
        try {
            setSubmitting(true);
            await supportService.submitBugReport({ title: t, description: d, severity });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                title="Report a bug"
                className={`relative p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
                <Bug className="w-5 h-5" />
            </button>

            {open && (
                <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
                        {/* Header */}
                        <div className="bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 p-5 text-white relative">
                            <button
                                onClick={close}
                                disabled={submitting}
                                className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-xl">
                                    <Bug className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold">Report a bug</h2>
                                    <p className="text-xs text-white/80 mt-0.5">Help us fix issues faster — we'll email this to the support team.</p>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        {success ? (
                            <div className="p-8 text-center">
                                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mb-4">
                                    <CheckCircle className="w-7 h-7 text-white" />
                                </div>
                                <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Thanks for the report</h3>
                                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Our team has been notified. You'll hear back if we need more details.
                                </p>
                                <button
                                    onClick={close}
                                    className="mt-5 px-6 py-2 rounded-lg bg-gradient-to-r from-rose-600 to-red-600 text-white text-sm font-medium hover:from-rose-700 hover:to-red-700"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="p-5 space-y-4">
                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        maxLength={120}
                                        placeholder="Short summary of the issue"
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition focus:ring-2 ${
                                            isDark
                                                ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500 focus:border-rose-500 focus:ring-rose-500/30'
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-rose-500 focus:ring-rose-500/30'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Description
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={5}
                                        maxLength={2000}
                                        placeholder="What were you doing? What did you expect? What happened instead?"
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none resize-none transition focus:ring-2 ${
                                            isDark
                                                ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500 focus:border-rose-500 focus:ring-rose-500/30'
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-rose-500 focus:ring-rose-500/30'
                                        }`}
                                    />
                                    <div className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                        {description.length}/2000
                                    </div>
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Severity
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {SEVERITIES.map(opt => {
                                            const active = severity === opt.value;
                                            return (
                                                <button
                                                    type="button"
                                                    key={opt.value}
                                                    onClick={() => setSeverity(opt.value)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                                                        active
                                                            ? `${opt.color} ring-2 ring-offset-1 ring-current dark:ring-offset-gray-800`
                                                            : isDark
                                                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {error && (
                                    <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${isDark ? 'bg-red-900/20 border border-red-800 text-red-200' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="flex justify-end gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={close}
                                        disabled={submitting}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                            isDark
                                                ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                        } disabled:opacity-50`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                <Bug className="w-4 h-4" />
                                                Send report
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default BugReportButton;
